// Custom hook do zarządzania logiką HydraAIAssistant

import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { useSelector, useDispatch, reduxActions, store} from 'store';
import {
  axios,
  createThread,
  updateThreadContext,
  addMessage,
  getThreadContent,
  addAttachment,
  removeAttachment,
  cancelProcessing,
  removeLastMessage as removeLastMessageApi,
  getAttachments,
  type ThreadContext,
  type ChatResponse,
  type ChatAttachment
} from 'api';
import { AssistantInfo, Message } from 'components/containers/ChatBase/HydraAIAssistant.types';
import {
  translateI18nTags,
  extractLastTurnId,
  replaceUrlPlaceholders,
  mergeIncrementalContent,
  computeContentHash,
  getBaseUrl,
  arrayBufferToBase64,
  simpleParseMessages
} from 'components/containers/ChatBase/helpers';

const logError = (message: string, error?: unknown) => {
  if (process.env.NODE_ENV === 'development') {
    // eslint-disable-next-line no-console
    console.error(message, error);
  }
};

interface UseHydraAIAssistantProps {
  availableAssistants: AssistantInfo[];
  urlParameters: Record<string, string>;
  apiBaseUrl?: string;
}

export const useHydraAIAssistant = ({
  availableAssistants = [],
  urlParameters = {},
  apiBaseUrl
}: UseHydraAIAssistantProps) => {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const { profile } = useSelector((state) => state.auth);

  const chatApiBaseUrl = apiBaseUrl || process.env.REACT_APP_CHAT_API_BASE_URL || 'http://localhost:5427';

  // State
  const [userMessage, setUserMessage] = useState('');
  const [threadId, setThreadId] = useState<string>('');
  const [conversationContent, setConversationContent] = useState('');
  const [selectedAssistant, setSelectedAssistant] = useState('');
  const [isInitialized, setIsInitialized] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [includeDetails, setIncludeDetails] = useState(false);
  const [attachedFiles, setAttachedFiles] = useState<File[]>([]);
  const [showFileUpload, setShowFileUpload] = useState(false);
  const [fileNameToAttachmentId, setFileNameToAttachmentId] = useState<Record<string, string>>({});
  const [filteredAssistants, setFilteredAssistants] = useState<AssistantInfo[]>([]);
  const [lastReceivedTurnId, setLastReceivedTurnId] = useState<string | null>(null);
  const [isIframeFullscreen, setIsIframeFullscreen] = useState(false);

  // Refs
  const chatDivRef = useRef<HTMLDivElement>(null);
  const textAreaRef = useRef<HTMLTextAreaElement>(null);
  const assistantDropdownRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const processingAbortControllerRef = useRef<AbortController | null>(null);
  const conversationContentRef = useRef<string>('');
  const lastReceivedTurnIdRef = useRef<string | null>(null);
  const isThreadLoadingRef = useRef<boolean>(false);
  const hasThreadBeenLoadedRef = useRef<boolean>(false);
  const fileNameToAttachmentIdRef = useRef<Record<string, string>>({});
  const attachedFilesRef = useRef<File[]>([]);

  const dispatch = useDispatch()

  // Computed properties
  const isDeveloperMode = urlParameters?.devel === '1' || process.env.REACT_APP_DEVELOPER_MODE || process.env.NODE_ENV === 'development';
  const showAssistantDropdown = filteredAssistants.length > 1;
  const attachedFilesCount = attachedFiles.length;

  // Synchronizuj refs ze state
  useEffect(() => {
    conversationContentRef.current = conversationContent;
  }, [conversationContent]);

  useEffect(() => {
    lastReceivedTurnIdRef.current = lastReceivedTurnId;
  }, [lastReceivedTurnId]);

  useEffect(() => {
    fileNameToAttachmentIdRef.current = fileNameToAttachmentId;
  }, [fileNameToAttachmentId]);

  useEffect(() => {
    attachedFilesRef.current = attachedFiles;
  }, [attachedFiles]);

  // Helper functions
  // Optimized smooth scrolling for streaming experience
  const scrollToBottom = useCallback(() => {
    if (chatDivRef.current) {
      // Use immediate scroll for better responsiveness during streaming
      const element = chatDivRef.current;
      // Scroll immediately for instant feedback
      element.scrollTop = element.scrollHeight;
      // Then use RAF for smooth animation if needed
      requestAnimationFrame(() => {
        if (element) {
          element.scrollTop = element.scrollHeight;
        }
      });
    }
  }, []);

  const getCurrentThreadContext = useCallback((): ThreadContext => {
    const currentPage = location.pathname;
    const userLanguage = i18n.language.split('-')[0] || 'pl';

    return {
      userName: profile ? `${profile.first_name} ${profile.last_name}`.trim() : '',
      currentModuleName: '',
      currentPage: '',
      fullUrl: currentPage,
      userLanguage
    };
  }, [location.pathname, i18n.language, profile]);

  // API wrapper functions
  const createChatThread = useCallback(async (threadContext: ThreadContext): Promise<string> => {
    return await createThread({ context: threadContext });
  }, []);

  const updateThreadContextWrapper = useCallback(async (threadId: string, threadContext: ThreadContext): Promise<void> => {
    await updateThreadContext(threadId, { context: threadContext });
  }, []);

  const addChatMessage = useCallback(async (
    threadId: string,
    message: string,
    assistant: string,
    urlParams: Record<string, string>
  ): Promise<void> => {
    const requestBody: { message: string; urlParameters?: Record<string, string> } = {
      message: message || ''
    };
    
    if (urlParams && Object.keys(urlParams).length > 0) {
      requestBody.urlParameters = urlParams;
    }
    
    await addMessage(threadId, assistant, requestBody);
  }, []);

  const getChatThreadContent = useCallback(async (
    threadId: string,
    includeDetails: number,
    lastTurnId: string,
    contentHash: string
  ): Promise<ChatResponse> => {
    return await getThreadContent(threadId, includeDetails, lastTurnId, contentHash);
  }, []);

  const cancelChatProcessing = useCallback(async (threadId: string): Promise<void> => {
    await cancelProcessing(threadId);
  }, []);

  const removeLastUserMessage = useCallback(async (threadId: string): Promise<Message | null> => {
    const result = await removeLastMessageApi(threadId);
    return result || null;
  }, []);

  const addChatAttachment = useCallback(async (
    threadId: string,
    fileName: string,
    contentType: string,
    size: number,
    fileContent: ArrayBuffer
  ): Promise<string> => {
    const base64Content = arrayBufferToBase64(fileContent);

    const attachmentId = await addAttachment(threadId, {
      fileName,
      contentType,
      size,
      content: base64Content
    });
    
    if (typeof attachmentId === 'string') {
      return attachmentId.trim().replace(/^"|"$/g, '');
    }
    return attachmentId;
  }, []);

  const removeChatAttachment = useCallback(async (threadId: string, attachmentId: string): Promise<void> => {
    await removeAttachment(threadId, attachmentId);
  }, []);

  const getChatAttachments = useCallback(async (threadId: string): Promise<ChatAttachment[]> => {
    return await getAttachments(threadId);
  }, []);

  // Thread management
  const loadHistory = useCallback(async (threadIdToLoad?: string) => {
    const targetThreadId = threadIdToLoad || threadId;
    if (!targetThreadId) {
      return;
    }

    try {
      const response = await getChatThreadContent(targetThreadId, includeDetails ? 1 : 0, 'none', 'none');
      let content = replaceUrlPlaceholders(
        response.chunk,
        getBaseUrl(),
        chatApiBaseUrl
      );

      content = translateI18nTags(content, t);
      setConversationContent(content);
      conversationContentRef.current = content;

      const newLastTurnId = extractLastTurnId(content);
      if (newLastTurnId) {
        setLastReceivedTurnId(newLastTurnId);
        lastReceivedTurnIdRef.current = newLastTurnId;
      }

      // Check if thread was lost (empty currentAssistantName indicates thread was lost after server restart)
      // Only show error if there's conversation history (thread had messages before)
      const hasHistory = content && content.trim().length > 0;
      if (hasHistory && (response.currentAssistantName === '' || response.currentAssistantName === null || response.currentAssistantName === undefined)) {
        dispatch(reduxActions.setChatThreadLost(true));
        dispatch(reduxActions.setIsChatHeaderVisible(true));
      } else {
        // Clear thread lost error if assistant name is present or no history exists
        dispatch(reduxActions.setChatThreadLost(false));
      }
    } catch (error) {
      logError('Error loading chat history:', error);
    }
  }, [threadId, includeDetails, chatApiBaseUrl, t, getChatThreadContent]);

  const startNewThread = useCallback(async () => {
    if (isThreadLoadingRef.current) {
      return;
    }

    isThreadLoadingRef.current = true;

    try {
      // Clear attachments
      if (attachedFiles.length > 0) {
        for (const fileName of Object.keys(fileNameToAttachmentId)) {
          const attachmentId = fileNameToAttachmentId[fileName];
          try {
            await removeChatAttachment(threadId || '', attachmentId);
          } catch {
            // Ignore errors
          }
        }
        setAttachedFiles([]);
        setFileNameToAttachmentId({});
      }

      const threadContext = getCurrentThreadContext();
      const newThreadId = await createChatThread(threadContext);
      setThreadId(newThreadId);
      
      if (isInitialized) {
        localStorage.setItem('chatThreadId', newThreadId);
      }

      setConversationContent('');
      conversationContentRef.current = '';
      setLastReceivedTurnId(null);
      lastReceivedTurnIdRef.current = null;
      
      // Clear thread lost error when starting new thread
      dispatch(reduxActions.setChatThreadLost(false));

      // Pokaż chat header ponownie po wyczyszczeniu konwersacji
      dispatch(reduxActions.setIsChatHeaderVisible(true));
    } catch (error) {
      logError('Error creating new chat thread:', error);
    } finally {
      isThreadLoadingRef.current = false;
    }
  }, [
    attachedFiles.length,
    fileNameToAttachmentId,
    threadId,
    isInitialized,
    getCurrentThreadContext,
    createChatThread,
    removeChatAttachment
  ]);

  const updateThreadContextAsync = useCallback(async () => {
    if (!threadId) return;

    try {
      const threadContext = getCurrentThreadContext();
      await updateThreadContextWrapper(threadId, threadContext);
    } catch (error) {
      logError('Error updating chat thread context:', error);
    }
  }, [threadId, getCurrentThreadContext, updateThreadContextWrapper]);

  const sendMessage = useCallback(async (messageOverride?: string) => {
    const messageToProcess = messageOverride !== undefined ? messageOverride : userMessage;
    
    // Allow sending if there's a message OR if there are attached files
    if (!messageToProcess.trim() && attachedFiles.length === 0) {
      return;
    }
    
    // Ukryj chat header po wysłaniu pierwszej wiadomości
    dispatch(reduxActions.setIsChatHeaderVisible(false));
    
    let currentThreadId = threadId || localStorage.getItem('chatThreadId') || '';
    
    if (!currentThreadId) {
      try {
        const threadContext = getCurrentThreadContext();
        currentThreadId = await createChatThread(threadContext);
        
        if (!currentThreadId) {
          logError('Failed to create thread');
          return;
        }
        
        setThreadId(currentThreadId);
        localStorage.setItem('chatThreadId', currentThreadId);
      } catch (error) {
        logError('Error creating thread:', error);
        return;
      }
    }

    const messageToSend = messageToProcess;
    
    // Wyczyść pole input tylko jeśli nie używamy override
    if (messageOverride === undefined) {
      setUserMessage('');
      if (textAreaRef.current) {
        textAreaRef.current.value = '';
      }
    }

    setIsProcessing(true);
    let emptyChunkCount = 0;

    const abortController = new AbortController();
    processingAbortControllerRef.current = abortController;

    try {
      // Sprawdź i odśwież token przed wysłaniem wiadomości
      // Proste zapytanie GET, które w przypadku braku autoryzacji spowoduje odświeżenie tokena przez axios interceptor
      try {
        await axios.get('/users/profile');
      } catch (error) {
        // Ignoruj błędy - interceptor axios automatycznie odświeży token jeśli potrzeba
        // Jeśli token nie może być odświeżony, interceptor obsłuży to odpowiednio
      }

      const sessionId = store.getState().auth.sessionId;
      const urlParamsWithSession = {
        ...urlParameters,
        ...(sessionId ? { session_id: sessionId } : {})
      };
      
      await addChatMessage(currentThreadId, messageToSend, selectedAssistant, urlParamsWithSession);

      // Wyczyść załączniki po wysłaniu wiadomości
      if (attachedFiles.length > 0) {
        setAttachedFiles([]);
        setFileNameToAttachmentId({});
      }

      // Poll for response
      let pollIteration = 0;
      while (!abortController.signal.aborted) {
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        pollIteration++;
        
        const currentContentHash = lastReceivedTurnIdRef.current
          ? computeContentHash(conversationContentRef.current, lastReceivedTurnIdRef.current)
          : 'none';

        try {
          const lastTurnIdToUse = lastReceivedTurnIdRef.current || 'none';
          
          const response = await getChatThreadContent(
            currentThreadId,
            includeDetails ? 1 : 0,
            lastTurnIdToUse,
            currentContentHash
          );

          if (response.chunk) {
            emptyChunkCount = 0;
            
            let processedContent = replaceUrlPlaceholders(
              response.chunk,
              getBaseUrl(),
              chatApiBaseUrl
            );

            processedContent = translateI18nTags(processedContent, t);

            if (response.fromTurnId) {
              setConversationContent((prev) => {
                // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                const merged = mergeIncrementalContent(prev, processedContent, response.fromTurnId!);
                conversationContentRef.current = merged;
                const newLastTurnId = extractLastTurnId(merged);
                if (newLastTurnId) {
                  setLastReceivedTurnId(newLastTurnId);
                  lastReceivedTurnIdRef.current = newLastTurnId;
                }
              return merged;
            });
          } else {
            setConversationContent(processedContent);
            conversationContentRef.current = processedContent;
            const newLastTurnId = extractLastTurnId(processedContent);
            if (newLastTurnId) {
              setLastReceivedTurnId(newLastTurnId);
              lastReceivedTurnIdRef.current = newLastTurnId;
            }
          }

          // Smooth scrolling for better streaming experience
          // Use double RAF for smoother updates during rapid content changes
          requestAnimationFrame(() => {
            requestAnimationFrame(() => {
              scrollToBottom();
            });
          });
          } else {
            emptyChunkCount++;
          }

          if (response.currentAssistantName && response.currentAssistantName !== selectedAssistant) {
            setSelectedAssistant(response.currentAssistantName);
          }

          // Check if thread was lost (empty currentAssistantName indicates thread was lost after server restart)
          // Only show error if there's conversation history (thread had messages before)
          const hasHistory = conversationContentRef.current && conversationContentRef.current.trim().length > 0;
          if (hasHistory && (response.currentAssistantName === '' || response.currentAssistantName === null || response.currentAssistantName === undefined)) {
            dispatch(reduxActions.setChatThreadLost(true));
            dispatch(reduxActions.setIsChatHeaderVisible(true));
          } else {
            // Clear thread lost error if assistant name is present or no history exists
            dispatch(reduxActions.setChatThreadLost(false));
          }

          if (response.isDone) {
            break;
          }
          
          if (emptyChunkCount > 120) {
            break;
          }

          // Adaptive delay: fast when content is streaming, slower when waiting
          // This balances smooth UX with server load
          // - 100ms when receiving chunks (active streaming)
          // - 300ms when no chunks (waiting for response)
          const delay = response.chunk ? 100 : 300;

          await new Promise((resolve, reject) => {
            const timeoutId = setTimeout(resolve, delay);
            abortController.signal.addEventListener('abort', () => {
              clearTimeout(timeoutId);
              reject(new Error('Aborted'));
            }, { once: true });
          }).catch(() => {
            // Timeout was aborted, break the loop
            return Promise.reject(new Error('Aborted'));
          });
        } catch (error: any) {
          if (abortController.signal.aborted || error?.message === 'Aborted') {
            break;
          }
          logError('Error in polling iteration:', error);
          throw error;
        }
      }
    } catch (error: any) {
      logError('Error sending message:', error);
    } finally {
      setIsProcessing(false);
      processingAbortControllerRef.current = null;
    }
  }, [
    userMessage,
    attachedFiles.length,
    threadId,
    selectedAssistant,
    includeDetails,
    urlParameters,
    chatApiBaseUrl,
    t,
    getCurrentThreadContext,
    createChatThread,
    addChatMessage,
    getChatThreadContent,
    scrollToBottom
  ]);

  const stopProcessing = useCallback(async () => {
    if (processingAbortControllerRef.current) {
      processingAbortControllerRef.current.abort();
    }
    setIsProcessing(false);

    if (threadId) {
      try {
        await cancelChatProcessing(threadId);
      } catch (error) {
        logError('Error cancelling processing:', error);
      }
    }
  }, [threadId, cancelChatProcessing]);

  const removeLastMessage = useCallback(async () => {
    if (!threadId) return;

    try {
      await removeLastUserMessage(threadId);
      await loadHistory();
    } catch (error) {
      logError('Error removing last message:', error);
    }
  }, [threadId, removeLastUserMessage, loadHistory]);

  // Assistant management
  const filterAssistants = useCallback(() => {
    const visibleAssistantsParam = urlParameters?.visible_assistants;
    let filtered = availableAssistants;

    if (visibleAssistantsParam) {
      const visibleNames = visibleAssistantsParam.split(',').map(s => s.trim().toLowerCase());
      filtered = availableAssistants.filter(a => 
        visibleNames.includes(a.name.toLowerCase()) || 
        visibleNames.includes(a.symbol.toLowerCase())
      );
    }

    setFilteredAssistants(filtered);
  }, [availableAssistants, urlParameters]);

  const findAssistantByNameOrSymbol = useCallback((nameOrSymbol: string): AssistantInfo | null => {
    const search = nameOrSymbol.toLowerCase();
    return filteredAssistants.find(
      a => a.name.toLowerCase() === search || a.symbol.toLowerCase() === search
    ) || null;
  }, [filteredAssistants]);

  const selectAssistant = useCallback((assistantName: string) => {
    setSelectedAssistant(assistantName);
    if (isInitialized) {
      localStorage.setItem('selectedAssistant', assistantName);
    }
  }, [isInitialized]);

  const canCurrentAssistantAttachFiles = useCallback((): boolean => {
    const assistant = filteredAssistants.find(a => a.name === selectedAssistant);
    return assistant?.canAttachFiles ?? false;
  }, [filteredAssistants, selectedAssistant]);

  // File management
  const loadAttachments = useCallback(async () => {
    if (!threadId) return;

    try {
      const attachments = await getChatAttachments(threadId);
      const mapping: Record<string, string> = {};
      attachments.forEach(att => {
        mapping[att.fileName] = att.id;
      });
      setFileNameToAttachmentId(mapping);
    } catch (error) {
      logError('Error loading attachments:', error);
    }
  }, [threadId, getChatAttachments]);

  const handleFileChange = useCallback(async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;

    const newFiles = Array.from(files);

    // Usuń stare pliki - w kolejce ma być tylko najnowszy
    // Użyj ref do przechowania aktualnych wartości przed aktualizacją
    const oldFiles = [...attachedFilesRef.current];
    const oldMapping = { ...fileNameToAttachmentIdRef.current };

    // Zastąp stare pliki nowymi
    setAttachedFiles(newFiles);

    // Wyczyść mapowanie - zostanie wypełnione nowymi poniżej
    setFileNameToAttachmentId({});

    // Usuń stare załączniki z serwera
    if (threadId && oldFiles.length > 0) {
      oldFiles.forEach(oldFile => {
        const attachmentId = oldMapping[oldFile.name];
        if (attachmentId) {
          removeChatAttachment(threadId, attachmentId).catch(error => {
            logError('Error removing old attachment:', error);
          });
        }
      });
    }

    // Dodaj nowe pliki do serwera
    if (threadId && newFiles.length > 0) {
      for (const file of newFiles) {
        try {
          const arrayBuffer = await file.arrayBuffer();
          const attachmentId = await addChatAttachment(
            threadId,
            file.name,
            file.type,
            file.size,
            arrayBuffer
          );

          setFileNameToAttachmentId(prev => ({
            ...prev,
            [file.name]: attachmentId
          }));
        } catch (error) {
          logError('Error uploading attachment:', error);
        }
      }
    }

    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  }, [threadId, addChatAttachment, removeChatAttachment]);

  const handleFileRemoved = useCallback(async (fileName: string) => {
    const attachmentId = fileNameToAttachmentId[fileName];
    if (attachmentId && threadId) {
      try {
        await removeChatAttachment(threadId, attachmentId);
      } catch (error) {
        logError('Error removing attachment:', error);
      }
    }

    setFileNameToAttachmentId(prev => {
      const newMapping = { ...prev };
      delete newMapping[fileName];
      return newMapping;
    });
  }, [fileNameToAttachmentId, threadId, removeChatAttachment]);

  // Thread loading
  const loadOrCreateThread = useCallback(async () => {
    if (!isInitialized || !chatApiBaseUrl) {
      return;
    }

    try {
      const storedThreadId = localStorage.getItem('chatThreadId');

      if (storedThreadId) {
        setThreadId(storedThreadId);
        await loadHistory(storedThreadId); // Przekaż threadId bezpośrednio

        // Small delay to let state update - use RAF instead of setTimeout
        await new Promise((resolve) => requestAnimationFrame(() => requestAnimationFrame(resolve)));

        if (!conversationContentRef.current || conversationContentRef.current.trim() === '') {
          await startNewThread();
        } else {
          await updateThreadContextAsync();
        }
      } else {
        await startNewThread();
      }
    } catch (error) {
      logError('Error in loadOrCreateThread:', error);
    } finally {
      isThreadLoadingRef.current = false;
    }
  }, [isInitialized, chatApiBaseUrl, loadHistory, startNewThread, updateThreadContextAsync]);

  // Event handlers
  const handleToggleFullscreen = useCallback(() => {
    setIsIframeFullscreen((prev) => {
      const newValue = !prev;

      try {
        if (window.parent !== window) {
          window.parent.postMessage(
            {
              type: 'hydra-ai-fullscreen-change',
              isFullscreen: newValue
            },
            '*'
          );
        }
      } catch (error) {
        logError('Could not notify parent about fullscreen change:', error);
      }

      return newValue;
    });
  }, []);

  const handleKeyDown = useCallback((e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey && !e.ctrlKey && !e.metaKey) {
      e.preventDefault();
      // Allow sending if there's a message OR if there are attached files
      if (!isProcessing && (userMessage.trim() || attachedFiles.length > 0)) {
        sendMessage();
      }
    }
  }, [isProcessing, userMessage, attachedFiles.length, sendMessage]);

  // Effects
  useEffect(() => {
    filterAssistants();
  }, [availableAssistants, urlParameters]); // Use direct dependencies instead of callback

  useEffect(() => {
    if (filteredAssistants.length > 0) {
      if (!selectedAssistant || urlParameters?.default_assistant) {
        if (urlParameters?.default_assistant) {
          const foundAssistant = findAssistantByNameOrSymbol(urlParameters.default_assistant);
          if (foundAssistant && filteredAssistants.includes(foundAssistant)) {
            setSelectedAssistant(foundAssistant.name);
          } else {
            const defaultAssistant = filteredAssistants.find((a) => a.isDefault);
            setSelectedAssistant(defaultAssistant?.name || filteredAssistants[0]?.name || '');
          }
        } else if (!selectedAssistant) {
          const defaultAssistant = filteredAssistants.find((a) => a.isDefault);
          setSelectedAssistant(defaultAssistant?.name || filteredAssistants[0]?.name || '');
        }

        if (filteredAssistants.length === 1) {
          setSelectedAssistant(filteredAssistants[0].name);
        }
      }
    }
  }, [filteredAssistants, urlParameters?.default_assistant, selectedAssistant, findAssistantByNameOrSymbol]);

  useEffect(() => {
    if (isInitialized && filteredAssistants.length > 0 && selectedAssistant && !threadId && !hasThreadBeenLoadedRef.current) {
      hasThreadBeenLoadedRef.current = true;
      loadOrCreateThread();
      loadAttachments();
    }
  }, [isInitialized, filteredAssistants.length, selectedAssistant, threadId]); // Removed function dependencies

  useEffect(() => {
    const init = async () => {
      try {
        if (!document.querySelector('script[src*="charts.js"]')) {
          const script = document.createElement('script');
          script.src = '/_content/Hydra.Shared/js/charts.js';
          script.async = true;
          // Ignoruj błąd ładowania skryptu - opcjonalna funkcjonalność
          script.onerror = () => {
            // Skrypt charts.js jest opcjonalny, błąd nie blokuje aplikacji
          };
          document.head.appendChild(script);
        }
      } catch (error) {
        // Ignoruj błąd - skrypt jest opcjonalny i może nie być dostępny
      }

      setIsInitialized(true);
    };

    init();

    const handleVisibilityChange = () => {
      if (!document.hidden && threadId) {
        // Use inline scroll instead of function ref to avoid dependencies
        if (chatDivRef.current) {
          requestAnimationFrame(() => {
            if (chatDivRef.current) {
              chatDivRef.current.scrollTop = chatDivRef.current.scrollHeight;
            }
          });
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [threadId]); // Removed loadHistory and scrollToBottom to prevent memory leak

  useEffect(() => {
    if (isInitialized && !urlParameters?.default_assistant && filteredAssistants.length > 0) {
      const storedAssistant = localStorage.getItem('selectedAssistant');
      if (storedAssistant && !selectedAssistant) {
        const foundAssistant = findAssistantByNameOrSymbol(storedAssistant);
        if (foundAssistant && filteredAssistants.includes(foundAssistant)) {
          setSelectedAssistant(foundAssistant.name);
        }
      }
    }
  }, [isInitialized, filteredAssistants, urlParameters?.default_assistant, selectedAssistant, findAssistantByNameOrSymbol]);

  useEffect(() => {
    if (threadId && isInitialized) {
      updateThreadContextAsync();
    }
  }, [location.pathname, threadId, isInitialized]); // Removed function dependency

  // Parse messages into bubbles - memoized to prevent re-parsing on every render
  const parsedMessages = useMemo(() => {
    return simpleParseMessages(conversationContent, includeDetails);
  }, [conversationContent, includeDetails]);

  return {
    // State
    userMessage,
    setUserMessage,
    threadId,
    conversationContent,
    selectedAssistant,
    isProcessing,
    includeDetails,
    setIncludeDetails,
    attachedFiles,
    setAttachedFiles,
    showFileUpload,
    setShowFileUpload,
    filteredAssistants,
    isIframeFullscreen,
    
    // Refs
    chatDivRef,
    textAreaRef,
    assistantDropdownRef,
    fileInputRef,
    
    // Computed
    isDeveloperMode,
    showAssistantDropdown,
    attachedFilesCount,
    
    // Functions
    sendMessage,
    stopProcessing,
    startNewThread,
    removeLastMessage,
    selectAssistant,
    canCurrentAssistantAttachFiles,
    handleFileChange,
    handleFileRemoved,
    handleToggleFullscreen,
    handleKeyDown,
    loadHistory,
    parsedMessages,
    t
  };
};

